import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Alert,
  Badge,
  Box,
  Button,
  Container,
  Group,
  Loader,
  Paper,
  Progress,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { CheckCircle, XCircle, ArrowRight, Trophy } from 'lucide-react'
import { RichText } from '../../components/common/RichText'
import { getImageSrc } from '../../utils/imageData'
import { stripLeadingQuestionNumber } from '../../utils/questionText'
import {
  assignmentsApi,
  AnswerSubmit,
  Assignment,
  AssignmentQuestion,
  AssignmentStudent,
  SubmissionDetailResponse,
} from '../../api/assignments'

// MC: question_set_id → choice_id (number); SPR: question_set_id → typed text (string)
type AnswerMap = Record<number, number | string>

function QuestionImages({ images }: { images: string[] | null | undefined }) {
  if (!images || images.length === 0) return null
  return (
    <>
      {images.map((src, idx) => {
        const imgSrc = getImageSrc(src)
        if (!imgSrc) return null
        return (
          <Box key={idx} my={8} style={{ textAlign: 'center' }}>
            <img
              src={imgSrc}
              alt={`Question figure ${idx + 1}`}
              style={{ maxWidth: '100%', borderRadius: 8, border: '1px solid var(--sat-border-soft)' }}
            />
          </Box>
        )
      })}
    </>
  )
}

function QuestionImage({ src }: { src: string | null | undefined }) {
  if (!src) return null
  const imgSrc = getImageSrc(src)
  if (!imgSrc) return null
  return (
    <Box my={8} style={{ textAlign: 'center' }}>
      <img
        src={imgSrc}
        alt="Question figure"
        style={{ maxWidth: '100%', borderRadius: 8, border: '1px solid var(--sat-border-soft)' }}
      />
    </Box>
  )
}

export function StudentPractice() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [assignment, setAssignment] = useState<Assignment | null>(null)
  const [studentRecord, setStudentRecord] = useState<AssignmentStudent | null>(null)
  const [questions, setQuestions] = useState<AssignmentQuestion[]>([])
  const [review, setReview] = useState<SubmissionDetailResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [currentIdx, setCurrentIdx] = useState(0)
  const [answers, setAnswers] = useState<AnswerMap>({})
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const startTimes = useRef<Record<number, number>>({})

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      try {
        const detail = await assignmentsApi.get(Number(id))
        if (cancelled) return

        const myStudent = detail.data.students[0] || null
        setAssignment(detail.data.assignment)
        setStudentRecord(myStudent)
        setQuestions(detail.data.questions)

        if (!myStudent) {
          return
        }

        if (
          (detail.data.assignment.grading_mode === 'auto' && myStudent.status === 'submitted') ||
          myStudent.status === 'graded' ||
          myStudent.status === 'reviewed'
        ) {
          const submission = await assignmentsApi.getSubmission(Number(id), myStudent.student_id)
          if (cancelled) return
          if (submission.data.can_view_answers) {
            setReview(submission.data)
            setSubmitted(true)
          }
          return
        }

        if (myStudent.status === 'assigned' || myStudent.status === 'processing') {
          await assignmentsApi.start(Number(id))
        }

        if (detail.data.questions.length > 0) {
          startTimes.current[detail.data.questions[0].id] = Date.now()
        }
      } catch {
        notifications.show({ title: 'Error', message: 'Failed to load assignment', color: 'red' })
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [id])

  const currentQ = questions[currentIdx]
  const progress = questions.length > 0 ? ((currentIdx + 1) / questions.length) * 100 : 0
  const displayPassage = stripLeadingQuestionNumber(currentQ?.passage_text)
  const displayPrompt = stripLeadingQuestionNumber(currentQ?.prompt)

  const selectAnswer = (choiceId: number) => {
    setAnswers((prev) => ({ ...prev, [currentQ.id]: choiceId }))
  }

  const setSprAnswer = (text: string) => {
    setAnswers((prev) => ({ ...prev, [currentQ.id]: text }))
  }

  const handleNext = () => {
    const nextIdx = currentIdx + 1
    if (nextIdx < questions.length) {
      setCurrentIdx(nextIdx)
      if (!startTimes.current[questions[nextIdx].id]) {
        startTimes.current[questions[nextIdx].id] = Date.now()
      }
    }
  }

  const handleSubmit = async () => {
    const unanswered = questions.filter((q) => {
      const ans = answers[q.id]
      return ans === undefined || ans === ''
    })
    if (unanswered.length > 0) {
      if (!confirm(`You have ${unanswered.length} unanswered question(s). Submit anyway?`)) return
    }
    setSubmitting(true)
    try {
      const payload: AnswerSubmit[] = questions
        .filter((q) => answers[q.id] !== undefined && answers[q.id] !== '')
        .map((q) => {
          const ans = answers[q.id]
          const timeSpent = startTimes.current[q.id]
            ? Math.round((Date.now() - startTimes.current[q.id]) / 1000)
            : 0
          if (q.question_type === 'spr') {
            return {
              question_set_id: q.id,
              selected_choice_id: null,
              text_answer: String(ans),
              time_spent_seconds: timeSpent,
            }
          }
          return {
            question_set_id: q.id,
            selected_choice_id: ans as number,
            time_spent_seconds: timeSpent,
          }
        })
      const res = await assignmentsApi.submit(Number(id), payload)
      if (res.data.result) {
        setReview(res.data.result)
      }
      setSubmitted(true)
    } catch (err: any) {
      notifications.show({ title: 'Error', message: err.response?.data?.detail || 'Failed to submit', color: 'red' })
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Loader color="eveningSea" />
      </Box>
    )
  }

  if (questions.length === 0) {
    return (
      <Container py={32}>
        <Alert color="yellow">This assignment has no questions.</Alert>
        <Button mt={12} variant="subtle" onClick={() => navigate('/assignments')}>Back</Button>
      </Container>
    )
  }

  if (!studentRecord) {
    return (
      <Container py={32}>
        <Alert color="yellow">This assignment has not been assigned to you.</Alert>
      </Container>
    )
  }

  if (submitted) {
    if (review) {
      return (
        <Container size="md" py={40}>
          <Stack gap={20}>
            <Paper shadow="md" radius="lg" p="xl">
              <Group justify="space-between" align="flex-start" mb={12}>
                <div>
                  <Title order={3} c="var(--sat-text)">Assignment Results</Title>
                  <Text c="var(--sat-text-soft)">{review.assignment.title}</Text>
                </div>
                <Badge color="green" variant="light">
                  Score {Math.round(review.student.score ?? review.auto_score)}%
                </Badge>
              </Group>
              <Text size="sm" c="var(--sat-text-soft)">
                {review.assignment.grading_mode === 'auto'
                  ? 'Correct answers and explanations are shown below because this assignment is auto-graded.'
                  : 'Your graded results, correct answers, and explanations are shown below.'}
              </Text>
              {review.student.tutor_feedback && (
                <Paper mt={16} p="md" radius="md" style={{ background: 'var(--sat-bg)' }}>
                  <Text size="xs" fw={700} c="var(--sat-text)" mb={4}>Tutor feedback</Text>
                  <Text size="sm" c="var(--sat-text-soft)">{review.student.tutor_feedback}</Text>
                </Paper>
              )}
            </Paper>

            {review.questions.map((question, index) => (
              <Paper key={question.id} shadow="sm" radius="lg" p="xl">
                <Stack gap={14}>
                  <Group justify="space-between" align="flex-start">
                    <Text fw={700} c="var(--sat-text)">Question {index + 1}</Text>
                    <Badge color={question.is_correct ? 'green' : 'red'} variant="light">
                      {question.is_correct ? 'Correct' : 'Incorrect'}
                    </Badge>
                  </Group>

                  {stripLeadingQuestionNumber(question.passage_text) && (
                    <Paper p="md" radius="md" style={{ background: 'var(--sat-bg)' }}>
                      <RichText size="sm">{stripLeadingQuestionNumber(question.passage_text)}</RichText>
                      <QuestionImages images={question.passage_images} />
                    </Paper>
                  )}

                  <RichText size="md">{stripLeadingQuestionNumber(question.prompt)}</RichText>
                  <QuestionImage src={question.prompt_image} />

                  {question.question_type === 'spr' ? (
                    <Stack gap={8}>
                      <Paper
                        p={14}
                        radius="md"
                        style={{
                          background: question.is_correct ? 'var(--sat-green-soft-subtle)' : 'var(--sat-red-soft)',
                          border: `2px solid ${question.is_correct ? 'var(--sat-green)' : 'var(--sat-red)'}`,
                        }}
                      >
                        <Group justify="space-between" align="center">
                          <div>
                            <Text size="xs" fw={700} c="var(--sat-text-faint)" mb={2}>Your answer</Text>
                            <Text fw={700} size="lg">{question.text_answer ?? '(no answer)'}</Text>
                          </div>
                          {question.is_correct
                            ? <CheckCircle size={20} color="var(--sat-green)" />
                            : <XCircle size={20} color="var(--sat-red)" />}
                        </Group>
                      </Paper>
                      {!question.is_correct && question.correct_answer_text && (
                        <Paper p={14} radius="md" style={{ background: 'var(--sat-green-soft-subtle)', border: '2px solid var(--sat-green)' }}>
                          <Text size="xs" fw={700} c="var(--sat-text-faint)" mb={2}>Correct answer</Text>
                          <Text fw={700} size="lg">{question.correct_answer_text}</Text>
                        </Paper>
                      )}
                    </Stack>
                  ) : (
                    <Stack gap={10}>
                      {question.choices.map((choice) => {
                        const isSelected = question.selected_choice_id === choice.id
                        const isCorrect = question.correct_choice_id === choice.id
                        const bg = isCorrect
                          ? 'var(--sat-green-soft-subtle)'
                          : isSelected
                            ? 'var(--sat-red-soft)'
                            : 'var(--sat-surface)'
                        const border = isCorrect
                          ? '2px solid var(--sat-green)'
                          : isSelected
                            ? '2px solid var(--sat-red)'
                            : '1px solid var(--sat-border-soft)'

                        return (
                          <Paper key={choice.id} p={14} radius="md" style={{ background: bg, border }}>
                            <Group gap={12} wrap="nowrap" align="flex-start">
                              <Text fw={700} size="sm" c="var(--sat-text-faint)" style={{ width: 20, flexShrink: 0 }}>
                                {choice.choice_label}
                              </Text>
                              <Box style={{ flex: 1, minWidth: 0 }}>
                                <RichText size="sm">{choice.choice_text}</RichText>
                              </Box>
                              {isCorrect && <CheckCircle size={18} color="var(--sat-green)" style={{ flexShrink: 0 }} />}
                              {!isCorrect && isSelected && <XCircle size={18} color="var(--sat-red)" style={{ flexShrink: 0 }} />}
                            </Group>
                          </Paper>
                        )
                      })}
                    </Stack>
                  )}

                  {question.explanation && (
                    <Paper p={14} radius="md" style={{ background: 'var(--sat-bg)', border: '1px solid var(--sat-blush)' }}>
                      <Text size="xs" fw={700} c="var(--sat-text)" mb={4}>Explanation</Text>
                      <RichText size="sm">{question.explanation}</RichText>
                    </Paper>
                  )}
                </Stack>
              </Paper>
            ))}

            <Group justify="center">
              <Button variant="outline" color="eveningSea" onClick={() => navigate(`/assignments/${id}`)}>
                Back to Assignment
              </Button>
              <Button color="grapefruit" onClick={() => navigate('/assignments')}>
                All Assignments
              </Button>
            </Group>
          </Stack>
        </Container>
      )
    }

    return (
      <Container size="sm" py={60}>
        <Paper shadow="md" radius="lg" p="xl" style={{ textAlign: 'center' }}>
          <Trophy size={64} color="var(--sat-yellow)" style={{ margin: '0 auto 16px' }} />
          <Text size="xl" fw={700} c="var(--sat-text)" mb={8}>Submitted!</Text>
          <Text c="var(--sat-text-soft)" mb={24}>
            You answered {Object.keys(answers).length} of {questions.length} questions.
            {assignment?.grading_mode === 'manual'
              ? ' Your tutor will review and grade your work.'
              : ' Your score has been recorded.'}
          </Text>
          <Group justify="center">
            <Button variant="outline" color="eveningSea" onClick={() => navigate(`/assignments/${id}`)}>
              View Assignment
            </Button>
            <Button color="grapefruit" onClick={() => navigate('/assignments')}>
              Back to Assignments
            </Button>
          </Group>
        </Paper>
      </Container>
    )
  }

  const isLast = currentIdx === questions.length - 1

  return (
    <Container size="sm" py={32}>
      <Group justify="space-between" mb={8}>
        <Text size="sm" c="var(--sat-text-soft)">Question {currentIdx + 1} of {questions.length}</Text>
        <Badge color="grape" variant="light">{Object.values(answers).filter((a) => a !== '').length} answered</Badge>
      </Group>
      <Progress value={progress} color="eveningSea" size="sm" radius="xl" mb={24} />

      <Paper shadow="md" radius="lg" p="xl" mb={20}>
        <Group gap={8} mb={16}>
          <Badge color="gray" variant="light" size="sm">D{currentQ.difficulty_level}</Badge>
          {assignment && (
            <Badge color={assignment.grading_mode === 'auto' ? 'green' : 'violet'} variant="light" size="sm">
              {assignment.grading_mode === 'auto' ? 'Auto-graded' : 'Manual-graded'}
            </Badge>
          )}
        </Group>

        {displayPassage && (
          <Paper p={14} radius="md" mb={16} style={{ background: 'var(--sat-bg)' }}>
            <RichText size="sm">{displayPassage}</RichText>
            <QuestionImages images={currentQ.passage_images} />
          </Paper>
        )}

        <RichText size="lg">{displayPrompt}</RichText>
        <QuestionImage src={currentQ.prompt_image} />

        {currentQ.question_type === 'spr' ? (
          <Box mt={8}>
            <Text size="sm" fw={600} c="var(--sat-text)" mb={8}>
              Enter your answer:
            </Text>
            <TextInput
              placeholder="Type your answer (e.g. 3, 2.5, 1/2)"
              value={typeof answers[currentQ.id] === 'string' ? (answers[currentQ.id] as string) : ''}
              onChange={(e) => setSprAnswer(e.currentTarget.value)}
              size="md"
              style={{ maxWidth: 240 }}
              styles={{
                input: {
                  background: 'var(--sat-surface)',
                  border: answers[currentQ.id] ? '2px solid var(--sat-green)' : '2px solid var(--sat-border-soft)',
                  fontWeight: 600,
                  fontSize: 18,
                  textAlign: 'center',
                },
              }}
            />
          </Box>
        ) : (
          <Stack gap={10}>
            {currentQ.choices.map((choice) => {
              const isSelected = answers[currentQ.id] === choice.id
              let bg = 'var(--sat-surface)'
              let border = '2px solid var(--sat-border-soft)'

              if (isSelected) {
                bg = 'var(--sat-green-soft)'
                border = '2px solid var(--sat-green)'
              }

              return (
                <Paper
                  key={choice.id}
                  p={14}
                  radius="md"
                  style={{ cursor: 'pointer', background: bg, border, transition: 'all 0.15s' }}
                  onClick={() => selectAnswer(choice.id)}
                >
                  <Group gap={12} wrap="nowrap" align="flex-start">
                    <Text fw={700} size="sm" c="var(--sat-text-faint)" style={{ width: 20, flexShrink: 0 }}>{choice.choice_label}</Text>
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <RichText size="sm">{choice.choice_text}</RichText>
                    </Box>
                    {isSelected && <CheckCircle size={18} color="var(--sat-green)" style={{ flexShrink: 0 }} />}
                  </Group>
                </Paper>
              )
            })}
          </Stack>
        )}
      </Paper>

      <Group justify="space-between">
        <Button variant="subtle" color="gray" disabled={currentIdx === 0} onClick={() => setCurrentIdx((i) => i - 1)}>
          ← Prev
        </Button>
        {!isLast ? (
          <Button color="grapefruit" rightSection={<ArrowRight size={16} />} onClick={handleNext}>
            Next
          </Button>
        ) : (
          <Button color="eveningSea" loading={submitting} onClick={handleSubmit}>
            Submit Assignment
          </Button>
        )}
      </Group>

      {/* Question nav dots */}
      <Group gap={6} mt={20} justify="center" wrap="wrap">
        {questions.map((q, i) => (
          <Box
            key={q.id}
            onClick={() => { setCurrentIdx(i); if (!startTimes.current[q.id]) startTimes.current[q.id] = Date.now() }}
            style={{
              width: 28, height: 28, borderRadius: '50%', display: 'flex',
              alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
              background: i === currentIdx ? 'var(--sat-red)' : (answers[q.id] !== undefined && answers[q.id] !== '') ? 'var(--sat-green)' : 'var(--sat-border-soft)',
              fontSize: 11, fontWeight: 600,
              color: i === currentIdx || answers[q.id] !== undefined ? 'var(--mantine-color-white)' : 'var(--sat-text-faint)',
            }}
          >
            {i + 1}
          </Box>
        ))}
      </Group>
    </Container>
  )
}
